/**
 * The "New" badge: which features are recent enough to be announced in the navigation, and
 * for how long each person keeps seeing the mark.
 *
 * ⚠️ **A registry, not a router.** Adding an entry here draws a badge on whatever already
 * renders that id; it creates no route, no page and no permission. The badge rides on a
 * navigation row that has ALREADY been filtered by permission, so this can never advertise a
 * screen somebody cannot open — and it must never be the only announcement of something that
 * changes what a person may see, because a decoration on a per-device clock is not a way to
 * tell people about an access change.
 *
 * ⚠️ **Two clocks, and the global one wins outright.**
 *
 *  - `since + GLOBAL_WINDOW_DAYS` — the feature stops being new for EVERYBODY, whether or not
 *    they ever looked. Without an outer bound, somebody who joins ARS in eighteen months opens
 *    the product to a row marked "New" about something two years old, and a badge that is
 *    always on is furniture nobody reads. It is the same reasoning that took the planned
 *    navigation entries behind a toggle (D-39) and that keeps a permanently single-option
 *    picker off the queue (D-116).
 *
 *  - `firstSeen + AFTER_FIRST_SEEN_DAYS` — how long the mark survives after somebody first
 *    goes there. Deliberately NOT "until first click": the point of the mark is to help
 *    somebody find a screen again while they are still getting used to it, and a badge that
 *    vanishes on the click that revealed it has helped with the one visit that needed no help.
 *
 * ⚠️ The global bound is not softened by the second clock — a first visit on day 13 ends on
 * day 14, not day 16. "Regardless" is the whole value of an outer bound; a bound a click can
 * push past is not one.
 *
 * ⚠️ **Per device, in `localStorage`, and safe here in the way D1 was not.** D1 kept a
 * PRIVILEGE in the browser and let it decide what an agent could do. This decides whether a
 * three-letter pill is drawn beside a row the reader can already open. Editing it cannot
 * reveal a screen, cannot hide one, and cannot change a single figure. It is the same argument
 * D-110 makes for the column layout, D-116 for the sticky team scope and D-155 for the desktop
 * nudge cadence.
 *
 * ⚠️ **The cost, stated because it is real:** dismissing the mark on a laptop does not dismiss
 * it on a phone. Accepted — the alternative is a table, a migration, an endpoint and an audit
 * decision for a decoration, and the global bound means the worst case is bounded at
 * {@link GLOBAL_WINDOW_DAYS} days either way.
 *
 * ⚠️ The clock is the reader's own workstation. A wrong one can only draw the pill when it
 * should not, or fail to; there is nothing here it can get wrong that matters.
 */

/** One announced feature. */
export interface NewFeature {
  /**
   * What the badge is about.
   *
   * ⚠️ Its own value rather than the route, so that moving a page does not silently re-announce
   * it to everybody whose stored entry no longer matches — and so that something which is not a
   * route (a tab, a panel) can be announced by the same mechanism.
   */
  readonly id: string;

  /**
   * The day it shipped, `YYYY-MM-DD`.
   *
   * ⚠️ Read as UTC midnight, so every reader's fourteen days start at the same instant rather
   * than at whatever midnight their own zone happened to reach first. For a decoration the zone
   * does not matter; being the SAME for everybody is what stops two colleagues disagreeing
   * about whether something is still new.
   */
  readonly since: string;
}

/**
 * Everything currently announced.
 *
 * ⚠️ Entries are DELETED once their window has closed rather than left to accumulate. A stale
 * list is one nobody trusts, and {@link isNewBadgeVisible} already refuses to draw them — so a
 * row left here is dead weight that reads as though it were doing something.
 */
export const NEW_FEATURES: readonly NewFeature[] = [
  // "My activity" (D-169) — the first screen in this product built for every employee at
  // ~29 sites, which is exactly the case a mark in the navigation exists for.
  { id: "my-activity", since: "2026-09-05" },

  // TARS, the conversational support agent on the new-request wizard — not a nav row, drawn
  // beside its own header instead, which is exactly the reuse this registry was built for.
  { id: "support-agent", since: "2026-09-07" },
];

/** How long a feature is announced at all, from {@link NewFeature.since}. */
export const GLOBAL_WINDOW_DAYS = 14;

/** How long the mark survives somebody's first visit. */
export const AFTER_FIRST_SEEN_DAYS = 3;

/** Where each person's first-visit stamps live. */
export const SEEN_STORAGE_KEY = "tf-new-seen";

const DAY_MS = 24 * 60 * 60 * 1000;

/** The instant a `YYYY-MM-DD` day begins in UTC, or null if it is not one. */
function startOfDayUtc(day: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;

  const at = Date.parse(day + "T00:00:00Z");
  return Number.isFinite(at) ? at : null;
}

/**
 * Whether the badge for this feature should be drawn.
 *
 * ⚠️ Pure, and it takes the clock and the stamp rather than reading either — the whole of the
 * rule is here where it can be tested at any date, and the browser wiring below is the part
 * with nothing to get wrong.
 *
 * @param firstSeenAtUtc when this reader first went there, or null if they never have.
 */
export function isNewBadgeVisible(
  feature: NewFeature,
  now: Date,
  firstSeenAtUtc: string | null,
): boolean {
  const since = startOfDayUtc(feature.since);

  // An unreadable date is not announced. It cannot happen — `invalidNewFeatureDates` fails the
  // build first — and if it ever did, silence is the right failure for a decoration.
  if (since === null) return false;

  const at = now.getTime();

  // Not out yet. A future entry is somebody staging an announcement, and a workstation clock
  // running behind lands here too: it draws nothing, which is the harmless direction.
  if (at < since) return false;

  // ⚠️ THE OUTER BOUND, TESTED ON ITS OWN AND BEFORE THE STAMP. Everything below can only take
  // the badge away, never give it back, so nothing after this line can extend the window past
  // it — which is what makes "fourteen days regardless" true rather than usually true.
  if (at >= since + GLOBAL_WINDOW_DAYS * DAY_MS) return false;

  if (firstSeenAtUtc === null) return true;

  const firstSeen = Date.parse(firstSeenAtUtc);

  // A stamp we cannot read is treated as no stamp: the reader keeps the mark for the rest of
  // the global window. Fails towards telling somebody about a feature, which is the direction
  // that costs nothing.
  if (!Number.isFinite(firstSeen)) return true;

  return at < firstSeen + AFTER_FIRST_SEEN_DAYS * DAY_MS;
}

/**
 * The stamps as they are safe to use: string values, everything else dropped.
 *
 * ⚠️ Anything at all can be in storage — another build's shape, a half-written value, something
 * a person typed into the console. Every one of those reads as "never visited", which draws the
 * badge; none of them can throw on a screen somebody was using.
 */
export function parseSeen(raw: unknown): Record<string, string> {
  if (typeof raw !== "string" || raw === "") return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};

  const seen: Record<string, string> = {};
  for (const [id, at] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof at === "string") seen[id] = at;
  }

  return seen;
}

/**
 * The stamps worth keeping.
 *
 * ⚠️ Written on every save so the key cannot grow without bound. A stamp for a feature that has
 * left the registry, or whose global window has closed, can never change what is drawn again —
 * {@link isNewBadgeVisible} refuses both — so keeping it is a record of somebody's navigation
 * with no purpose, which is not a thing to leave lying in a browser.
 */
export function pruneSeen(
  seen: Record<string, string>,
  features: readonly NewFeature[],
  now: Date,
): Record<string, string> {
  const kept: Record<string, string> = {};

  for (const feature of features) {
    const at = seen[feature.id];
    if (at === undefined) continue;

    const since = startOfDayUtc(feature.since);
    if (since === null) continue;
    if (now.getTime() >= since + GLOBAL_WINDOW_DAYS * DAY_MS) continue;

    kept[feature.id] = at;
  }

  return kept;
}

/**
 * Ids that claim to be announced and are not in the registry.
 *
 * ⚠️ The guard behind the badge, in the shape D-166 argued for: a name that resolves to nothing
 * is a claim the product cannot keep, and here it would be a row quietly drawing no badge with
 * nobody able to tell that from a window that has closed. Takes both lists as arguments so the
 * test can prove it CATCHES something as well as that the real registry is clean — with an
 * empty registry the assertion is otherwise vacuous, which is the defect D-166 exists for.
 */
export function unknownNewFeatureIds(
  ids: readonly string[],
  features: readonly NewFeature[],
): string[] {
  const known = new Set(features.map((f) => f.id));
  return ids.filter((id) => !known.has(id));
}

/** Registry entries whose `since` is not a readable `YYYY-MM-DD`. */
export function invalidNewFeatureDates(features: readonly NewFeature[]): string[] {
  return features.filter((f) => startOfDayUtc(f.since) === null).map((f) => f.id);
}

/** Registry entries sharing an id. */
export function duplicateNewFeatureIds(features: readonly NewFeature[]): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  for (const feature of features) {
    if (seen.has(feature.id)) duplicates.add(feature.id);
    seen.add(feature.id);
  }

  return [...duplicates];
}

// ---------------------------------------------------------------------------
// The browser side. Everything above is pure; everything below only reads and
// writes one key, and every failure of it falls to "draw the badge".
// ---------------------------------------------------------------------------

function readSeen(): Record<string, string> {
  try {
    return parseSeen(localStorage.getItem(SEEN_STORAGE_KEY));
  } catch {
    // Storage can be full, blocked by policy, or absent entirely.
    return {};
  }
}

/** Whether to draw the badge for `id` right now, for this reader on this device. */
export function isNew(id: string, now: Date = new Date()): boolean {
  const feature = NEW_FEATURES.find((f) => f.id === id);
  if (feature === undefined) return false;

  return isNewBadgeVisible(feature, now, readSeen()[id] ?? null);
}

/**
 * Record that this reader has been to the feature.
 *
 * ⚠️ **The first stamp wins.** Going back a second time must not push the three days out again,
 * or somebody who uses a new screen daily keeps its badge until the global bound — which is the
 * badge failing at its one job, telling people what is new rather than what they use.
 *
 * ⚠️ Idempotent and silent. It is called from a render effect that runs in both the sidebar and
 * the mobile sheet, so it is reached twice on a phone; and it must never surface an error on
 * whatever screen the person was actually using.
 */
export function markFeatureSeen(id: string, now: Date = new Date()): void {
  if (!NEW_FEATURES.some((f) => f.id === id)) return;

  try {
    const seen = readSeen();
    if (seen[id] !== undefined) return;

    const next = pruneSeen({ ...seen, [id]: now.toISOString() }, NEW_FEATURES, now);
    localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Nothing to do and nothing worth saying. The reader keeps the badge for the rest of the
    // global window, which is a fortnight of a small pill at worst.
  }
}
